import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider as MUIThemeProvider, Theme } from '@mui/material/styles';
import { doc, onSnapshot, collection, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Types pour les assets
export interface ThemeAsset {
  url: string;
  name?: string;
  path?: string; // Chemin dans le stockage Firebase pour suppression
}

export interface ThemeAssets {
  logos: ThemeAsset[];
  cursor?: ThemeAsset;
  impacts: ThemeAsset[];
  watermark?: ThemeAsset;
  cursorEnabled?: boolean;
  impactsEnabled?: boolean; // Optionnel, pour future utilisation
  watermarkEnabled?: boolean;
}

// Type complet d'un thème
export interface ThemeDefinition {
  id: string;
  name: string;
  startDate?: string; // ISO string YYYY-MM-DD
  endDate?: string;   // ISO string YYYY-MM-DD
  isDefault: boolean;
  colors: {
    background: {
        mode: 'solid' | 'gradient';
        color1: string;
        color2: string;
        gradientType: 'linear' | 'radial';
        angle: number;
        stop1: number;
        stop2: number;
    };
    filledButton: {
        text: string;
        background: string;
        border: string;
    };
    outlinedButton: {
        text: string;
        background: string;
        border: string;
    };
  };
  assets: ThemeAssets;
  threeJs?: {
    enabled?: boolean;
    style?: 'grid' | 'particles' | 'both';
    waveSpeed?: number;
    particleCount?: number;
  };
}

interface ThemeContextType {
  currentTheme: Theme;
  activeThemeDefinition: ThemeDefinition | null;
}

const ThemeContext = createContext<ThemeContextType>({} as ThemeContextType);

export const useTheme = () => useContext(ThemeContext);

// Définition du thème par défaut
const defaultThemeOptions = {
  palette: {
    mode: 'dark' as const,
    primary: {
      main: '#4a90e2',
      light: '#64a6ff',
      dark: '#2176d2',
    },
    secondary: {
      main: '#1e88e5',
      light: '#4b9fea',
      dark: '#115293',
    },
    background: {
      default: '#0a192f',
      paper: '#172a45',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.85)',
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 800, letterSpacing: '0.02em' },
    h2: { fontWeight: 700, letterSpacing: '0.01em' },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    button: { fontWeight: 600, letterSpacing: '0.05em' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: 'url("/images/RezoneArt/Rezone_Art1.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          '&:before': {
            content: '""',
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(135deg, rgba(10, 25, 47, 0.95) 0%, rgba(30, 136, 229, 0.8) 100%)',
            zIndex: -1,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(23, 42, 69, 0.95)',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid rgba(74, 144, 226, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 25, 47, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(74, 144, 226, 0.2)',
        },
      },
    },
  },
};

// Structure par défaut pour un nouveau thème
export const DEFAULT_THEME_DEF: ThemeDefinition = {
    id: 'default',
    name: 'Thème de base',
    isDefault: true,
    colors: {
        background: {
            mode: 'solid',
            color1: '#0a192f',
            color2: '#172a45',
            gradientType: 'linear',
            angle: 135,
            stop1: 0,
            stop2: 100,
        },
        filledButton: {
            text: '#ffffff',
            background: '#4a90e2',
            border: 'transparent',
        },
        outlinedButton: {
            text: '#4a90e2',
            background: 'transparent',
            border: '#4a90e2',
        },
    },
    assets: {
        logos: [],
        impacts: [],
        cursorEnabled: false,
        impactsEnabled: false,
    },
    threeJs: {
        enabled: true,
        style: 'both',
        waveSpeed: 0.5,
        particleCount: 800,
    }
};

// Utilitaire pour convertir hexadécimal en RGBA
export const hexToRgba = (hex: string, alpha: number) => {
  if (!hex) return `rgba(23, 42, 69, ${alpha})`;
  let cleanHex = hex.replace('#', '');
  if (cleanHex.length === 3) {
    cleanHex = cleanHex.split('').map(char => char + char).join('');
  }
  const r = parseInt(cleanHex.substring(0, 2), 16) || 0;
  const g = parseInt(cleanHex.substring(2, 4), 16) || 0;
  const b = parseInt(cleanHex.substring(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const CustomThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(createTheme(defaultThemeOptions));
  const [activeThemeDefinition, setActiveThemeDefinition] = useState<ThemeDefinition | null>(null);

  useEffect(() => {
    // Migration : Vérifier si 'default' existe dans 'themes', sinon copier depuis 'settings/theme'
    const checkMigration = async () => {
        try {
            const defaultThemeRef = doc(db, 'themes', 'default');
            const defaultThemeSnap = await getDoc(defaultThemeRef);
            
            if (!defaultThemeSnap.exists()) {
                // Tentative de récupération de l'ancien format
                const oldThemeRef = doc(db, 'settings', 'theme');
                const oldThemeSnap = await getDoc(oldThemeRef);
                
                let initialData = DEFAULT_THEME_DEF;
                
                if (oldThemeSnap.exists()) {
                    const oldData = oldThemeSnap.data();
                    // Mapping sommaire pour ne pas perdre les couleurs
                    initialData = {
                        ...DEFAULT_THEME_DEF,
                        colors: {
                            ...DEFAULT_THEME_DEF.colors,
                            ...oldData // On espère que la structure est compatible
                        } as any
                    };
                    // Correction si background était simple string
                    if (typeof (oldData as any).background === 'string') {
                        initialData.colors.background = {
                            ...DEFAULT_THEME_DEF.colors.background,
                            color1: (oldData as any).background,
                            mode: 'solid'
                        }
                    } else if ((oldData as any).background && !(oldData as any).background.mode) {
                         initialData.colors.background = {
                            ...DEFAULT_THEME_DEF.colors.background,
                            ...(oldData as any).background
                        }
                    } else if ((oldData as any).background) {
                        initialData.colors.background = (oldData as any).background;
                    }
                }
                
                await setDoc(defaultThemeRef, initialData);
            }
        } catch (e) {
            console.error("Erreur migration thème:", e);
        }
    };
    checkMigration();

    // Écoute de la collection 'themes'
    const unsubscribe = onSnapshot(collection(db, 'themes'), (snapshot) => {
      const themes: ThemeDefinition[] = [];
      snapshot.forEach((doc) => {
        themes.push({ id: doc.id, ...doc.data() } as ThemeDefinition);
      });

      // Déterminer le thème actif
      const getLocalDate = () => {
        const d = new Date();
        const offset = d.getTimezoneOffset();
        const local = new Date(d.getTime() - (offset * 60 * 1000));
        return local.toISOString().split('T')[0];
      };
      
      // Helper pour vérifier si une date est dans une plage (ignorant l'année)
      const checkDateInRange = (currentDateStr: string, startDateStr: string, endDateStr: string) => {
          try {
              // Extraire MM-DD
              const getCurrentMD = (d: string) => d.substring(5); // YYYY-MM-DD -> MM-DD
              
              const current = getCurrentMD(currentDateStr);
              const start = getCurrentMD(startDateStr);
              const end = getCurrentMD(endDateStr);

              if (start <= end) {
                  // Période normale (ex: 06-01 au 08-31)
                  return current >= start && current <= end;
              } else {
                  // Période à cheval sur l'année (ex: 12-15 au 01-15)
                  return current >= start || current <= end;
              }
          } catch (e) {
              console.error("Erreur parsing date thème", e);
              return false;
          }
      };
      
      const today = getLocalDate();
      console.log("[ThemeContext] Checking themes for date:", today);

      // 1. Chercher un thème temporaire actif
      const activeTempTheme = themes.find(t => {
        if (t.isDefault) return false;
        if (!t.startDate || !t.endDate) return false;
        
        const isActive = checkDateInRange(today, t.startDate, t.endDate);
        // console.log(`[ThemeContext] Theme "${t.name}" (${t.startDate} -> ${t.endDate}): ${isActive ? 'ACTIVE' : 'Inactive'}`);
        return isActive;
      });

      // 2. Sinon prendre le thème par défaut
      const defaultTheme = themes.find(t => t.isDefault) || DEFAULT_THEME_DEF;

      const winningTheme = activeTempTheme || defaultTheme;
      
      // Assurer que threeJs existe sur le thème actif
      if (winningTheme && !winningTheme.threeJs) {
        winningTheme.threeJs = DEFAULT_THEME_DEF.threeJs;
      } else if (winningTheme && winningTheme.threeJs) {
        winningTheme.threeJs = {
          enabled: winningTheme.threeJs.enabled !== undefined ? winningTheme.threeJs.enabled : DEFAULT_THEME_DEF.threeJs?.enabled,
          style: winningTheme.threeJs.style || DEFAULT_THEME_DEF.threeJs?.style,
          waveSpeed: winningTheme.threeJs.waveSpeed !== undefined ? winningTheme.threeJs.waveSpeed : DEFAULT_THEME_DEF.threeJs?.waveSpeed,
          particleCount: winningTheme.threeJs.particleCount !== undefined ? winningTheme.threeJs.particleCount : DEFAULT_THEME_DEF.threeJs?.particleCount,
        };
      }

      setActiveThemeDefinition(winningTheme);

      // 3. Construire le thème MUI
      const colors = winningTheme.colors;
      
      // Logique background
      let backgroundValue = defaultThemeOptions.palette.background.default;
      let isCustomBackground = false;
      
      if (colors.background) {
          isCustomBackground = true;
          const bg = colors.background;
          if (bg.mode === 'gradient') {
            if (bg.gradientType === 'radial') {
                backgroundValue = `radial-gradient(circle, ${bg.color1} ${bg.stop1}%, ${bg.color2} ${bg.stop2}%)`;
            } else {
                backgroundValue = `linear-gradient(${bg.angle || 135}deg, ${bg.color1} ${bg.stop1}%, ${bg.color2} ${bg.stop2}%)`;
            }
          } else {
              backgroundValue = bg.color1;
          }
      }

      // Construction du thème MUI
      const cardBg = hexToRgba(colors.background?.color2 || colors.background?.color1 || '#172a45', 0.45);
      const cardBorder = hexToRgba(colors.filledButton?.background || '#4a90e2', 0.15);
      const navBg = hexToRgba(colors.background?.color1 || '#0a192f', 0.65);
      const navBorder = hexToRgba(colors.filledButton?.background || '#4a90e2', 0.12);
      const paperBg = hexToRgba(colors.background?.color2 || '#172a45', 0.55);
      const paperBorder = hexToRgba(colors.filledButton?.background || '#4a90e2', 0.15);

      const newOptions = {
        ...defaultThemeOptions,
        palette: {
            ...defaultThemeOptions.palette,
            background: {
                ...defaultThemeOptions.palette.background,
                default: isCustomBackground ? backgroundValue : defaultThemeOptions.palette.background.default,
            }
        },
        components: {
            ...defaultThemeOptions.components,
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        ...defaultThemeOptions.components.MuiCssBaseline.styleOverrides.body,
                        background: 'transparent',
                        backgroundImage: 'none',
                        '&:before': { display: 'none' },
                        // Curseur personnalisé
                        ...(winningTheme.assets?.cursorEnabled && winningTheme.assets?.cursor ? {
                            cursor: `url(${winningTheme.assets.cursor.url}), auto`
                        } : {})
                    },
                    // Appliquer le curseur sur les boutons aussi
                    MuiButtonBase: {
                        styleOverrides: {
                            root: {
                                ...(winningTheme.assets?.cursorEnabled && winningTheme.assets?.cursor ? {
                                    cursor: `url(${winningTheme.assets.cursor.url}), pointer`
                                } : {})
                            }
                        }
                    }
                }
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '4px',
                        padding: '10px 30px',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        boxShadow: 'none',
                    },
                    contained: {
                        backgroundColor: colors.filledButton?.background,
                        color: colors.filledButton?.text,
                        border: colors.filledButton?.border ? `1px solid ${colors.filledButton.border}` : 'none',
                        '&:hover': {
                            backgroundColor: colors.filledButton?.background,
                            borderColor: colors.filledButton?.border,
                            filter: 'brightness(1.1)',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                        }
                    },
                    outlined: {
                        backgroundColor: colors.outlinedButton?.background || 'transparent',
                        color: colors.outlinedButton?.text,
                        border: colors.outlinedButton?.border ? `2px solid ${colors.outlinedButton.border}` : '1px solid currentColor',
                        '&:hover': {
                            backgroundColor: colors.outlinedButton?.background ? colors.outlinedButton.background : 'rgba(255,255,255,0.1)',
                            borderColor: colors.outlinedButton?.border,
                            filter: 'brightness(1.1)',
                        }
                    }
                }
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        backgroundColor: cardBg,
                        backdropFilter: 'blur(16px)',
                        borderRadius: '12px',
                        border: `1px solid ${cardBorder}`,
                        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
                        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out, border-color 0.3s ease-in-out',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 40px 0 ${hexToRgba(colors.filledButton?.background || '#4a90e2', 0.25)}`,
                            borderColor: hexToRgba(colors.filledButton?.background || '#4a90e2', 0.4),
                        },
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: navBg,
                        backdropFilter: 'blur(16px)',
                        borderBottom: `1px solid ${navBorder}`,
                        boxShadow: 'none',
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundColor: paperBg,
                        backdropFilter: 'blur(16px)',
                        border: `1px solid ${paperBorder}`,
                        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)',
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        backgroundColor: hexToRgba(colors.background?.color1 || '#0a192f', 0.9),
                        backdropFilter: 'blur(12px)',
                        border: `1px solid ${cardBorder}`,
                    }
                }
            },
            MuiPopover: {
                styleOverrides: {
                    paper: {
                        backgroundColor: hexToRgba(colors.background?.color2 || '#172a45', 0.9),
                        backdropFilter: 'blur(12px)',
                        border: `1px solid ${cardBorder}`,
                    }
                }
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        backgroundColor: hexToRgba(colors.background?.color1 || '#0a192f', 0.9),
                        backdropFilter: 'blur(16px)',
                        borderRight: `1px solid ${navBorder}`,
                    }
                }
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        backgroundColor: hexToRgba(colors.background?.color2 || '#172a45', 0.75),
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${hexToRgba(colors.filledButton?.background || '#4a90e2', 0.3)}`,
                        borderRadius: '16px',
                        boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
                        ...(winningTheme.assets?.watermarkEnabled && winningTheme.assets?.watermark ? {
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0, left: 0, right: 0, bottom: 0,
                                backgroundImage: `url("${winningTheme.assets.watermark.url}")`,
                                backgroundPosition: 'center',
                                backgroundSize: '200px auto',
                                backgroundRepeat: 'repeat',
                                opacity: 0.05,
                                zIndex: 0,
                                pointerEvents: 'none'
                            }
                        } : {}),
                        // Force Button styles inside Dialogs
                        '& .MuiButton-root': {
                            backgroundColor: colors.filledButton?.background,
                            color: colors.filledButton?.text,
                            border: colors.filledButton?.border ? `1px solid ${colors.filledButton.border}` : 'none',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: colors.filledButton?.background,
                                borderColor: colors.filledButton?.border,
                                filter: 'brightness(1.1)',
                                boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                            }
                        },
                        // Override for Cancel/Secondary buttons (not last child) -> Button 2 (Outlined)
                        '& .MuiButton-root:not(:last-of-type)': {
                            backgroundColor: colors.outlinedButton?.background || 'transparent',
                            color: colors.outlinedButton?.text,
                            border: colors.outlinedButton?.border ? `2px solid ${colors.outlinedButton.border}` : '1px solid currentColor',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: colors.outlinedButton?.background ? colors.outlinedButton.background : 'rgba(255,255,255,0.1)',
                                borderColor: colors.outlinedButton?.border,
                                filter: 'brightness(1.1)',
                                boxShadow: 'none',
                            }
                        }
                    } as any
                }
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        position: 'relative',
                        zIndex: 1
                    } as any
                }
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: {
                        position: 'relative',
                        zIndex: 1
                    } as any
                }
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        position: 'relative',
                        zIndex: 1
                    } as any
                }
            }
        }
      };

      setTheme(createTheme(newOptions));
    });

    return () => unsubscribe();
  }, []);

  return (
    <ThemeContext.Provider value={{ currentTheme: theme, activeThemeDefinition }}>
      <MUIThemeProvider theme={theme}>
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
